import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Link from "@amzn/awsui-components-react/polaris/link";
import { KB_APPROVALS_TAB_ID } from "pages/reviews/definitions";
import { Document } from "models/schemas";
import {
  useBatchRegisterDocumentsMutation,
  useBatchUpdateDocumentsMutation,
  useBatchDeleteDocumentsMutation,
  useUploadUrlMutation,
  useLazyDownloadUrlQuery,
  useLazySearchDocumentsQuery,
} from "./endpoints";
import {
  useIngestDocumentsMutation,
  useDeleteIngestedDocumentsMutation,
} from "pages/gaia-ai/endpoint";
import { useSubmitKBApprovalMutation } from "pages/gaia-ai/kb-approval-endpoints";
import {
  ExistingDocApprovalRouteState,
  DeletionApprovalRouteState,
} from "pages/kb-approval/definitions";
import { useAppSelector } from "app/store";
import { useSelectedTeam } from "features/team-settings/hooks";
import { GaiaConstants } from "utils/gaia-constants";
import {
  getFileExtension,
  getFileNameWithoutExtension,
  getFileSize,
} from "utils/date-format";
import {
  createUploadUrlRequest,
  buildS3Key,
  batchItems,
  sleep,
} from "utils/document-utils";
import {
  uploadFileWithProgress,
  UploadProgress,
  CancelToken,
} from "utils/upload-progress-utils";
import { DocumentCategory } from "./definitions";
import { UploadedDocument } from "components/input-files-modal/definitions";
import {
  isAnswersKbSupportedExtension,
  isExcelExtension,
} from "utils/accepted-file-types";
import { isLink } from "./utils";
import { getDocumentErrorMessage } from "./document-error-utils";
import { useDefaultDocumentType } from "features/team-settings/hooks";

// Excel-specific `content` string for the Gaia "Excel not added" notice. The
// Gaia guard filters on `isExcelExtension`, so Excel is the ONLY thing it drops
// and Excel-specific copy is accurate there. Named here rather than re-typed at
// the call sites.
const GAIA_EXCEL_KB_NOT_SUPPORTED =
  "Excel (.xls, .xlsx) files are not supported by the Gaia knowledge base and were not added.";

// Generic `content` strings for the Answers "unsupported file not added" notice.
// The Answers guard filters on `isAnswersKbSupportedExtension`, which drops ANY
// unsupported extension (or a doc with no extension) — not just Excel — so the
// copy must NOT name Excel. Wording mirrors the folder-ingest path's
// "unsupported" vocabulary so the two Answers-KB paths stay consistent. One
// string for the all-unsupported branch, one for the partial branch.
const ANSWERS_UNSUPPORTED_KB_NOT_SUPPORTED =
  "Files with an unsupported file type were not added to the Answers knowledge base.";
const ANSWERS_UNSUPPORTED_KB_NOT_SUPPORTED_PARTIAL =
  "Files with an unsupported file type were not added to the Answers knowledge base and were skipped.";

/**
 * Builds the "Excel not added to the knowledge base" info notification used by
 * the two Gaia Excel-guard sites (all-Excel + partial branches of
 * `ingestToGaiaKB`). The header switches between the singular/plural form on
 * `excludedExcelCount`; `content` is supplied by the caller. Module-level so it
 * stays out of any `useCallback` dependency array.
 */
const buildExcelNotAddedNotification = (
  excludedExcelCount: number,
  content: string,
) => ({
  type: "info",
  header:
    excludedExcelCount === 1
      ? "Excel file not added to the knowledge base."
      : "Excel files not added to the knowledge base.",
  content,
  dismissible: true,
  timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
});

/**
 * Builds the generic "file not added to the knowledge base" info notification
 * used by the two Answers-KB guard sites (all-unsupported + partial branches of
 * `ingestToAnswersKB`). Sibling of `buildExcelNotAddedNotification` with generic
 * (non-Excel) header copy, because the Answers guard drops any unsupported
 * extension rather than only Excel. The header switches between singular/plural
 * on `skippedUnsupportedCount`; `content` is supplied by the caller (all-
 * unsupported vs partial). Module-level so it stays out of any `useCallback`
 * dependency array.
 */
const buildUnsupportedNotAddedNotification = (
  skippedUnsupportedCount: number,
  content: string,
) => ({
  type: "info",
  header:
    skippedUnsupportedCount === 1
      ? "File not added to the knowledge base."
      : "Files not added to the knowledge base.",
  content,
  dismissible: true,
  timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
});

export interface FileOperationsOSProps {
  addNotification: (notification: any) => void;
  teamName: string;
  currentUser: string;
  parentId: string;
  gaiaObjectId?: string;
  category?: DocumentCategory;
}

/**
 * Progress event emitted by `ingestFoldersToAnswersKB` so a caller (the
 * DocumentsTableOS modal) can render live status. Two disjoint phases:
 *
 *   - "discovering": paginating SearchDocuments per selected folder. The
 *     modal caps its progress bar at 99% during this phase.
 *   - "ingesting": submission is in flight. Emitted exactly once at the
 *     start of the single ingestDocuments call so the modal can flip to a
 *     terminal spinner state.
 */
export type IngestProgress =
  | {
      phase: "discovering";
      foldersDiscovered: number;
      folderCount: number;
    }
  | {
      phase: "ingesting";
      eligibleCount: number;
      skippedAlreadyInKB: number;
      skippedUnsupported: number;
      skippedLinks: number;
    };

/**
 * Build the human-facing skip parts array used by both the completion
 * toast (file-operations-os) and the modal secondary line
 * (FolderKBIngestionModal). Only non-zero reasons appear, in a fixed order:
 * already-in-KB, unsupported extension, links. Pluralized on count.
 */
export function buildSkipParts(counts: {
  skippedAlreadyInKB: number;
  skippedUnsupported: number;
  skippedLinks: number;
}): string[] {
  const parts: string[] = [];
  if (counts.skippedAlreadyInKB > 0)
    parts.push(`${counts.skippedAlreadyInKB} already in KB`);
  if (counts.skippedUnsupported > 0)
    parts.push(
      `${counts.skippedUnsupported} unsupported extension${counts.skippedUnsupported === 1 ? "" : "s"}`,
    );
  if (counts.skippedLinks > 0)
    parts.push(
      `${counts.skippedLinks} link${counts.skippedLinks === 1 ? "" : "s"}`,
    );
  return parts;
}

export const useFileOperationsOS = (props: FileOperationsOSProps) => {
  const {
    addNotification,
    teamName,
    currentUser,
    parentId,
    gaiaObjectId,
    category = "team-files",
  } = props;

  const [uploadUrlCall] = useUploadUrlMutation();
  const [batchRegisterDocuments] = useBatchRegisterDocumentsMutation();
  const [batchUpdateDocuments] = useBatchUpdateDocumentsMutation();
  const [batchDeleteDocuments] = useBatchDeleteDocumentsMutation();
  const [lazyDownloadUrlCall] = useLazyDownloadUrlQuery();
  const [ingestDocuments] = useIngestDocumentsMutation();
  const [searchDocuments] = useLazySearchDocumentsQuery();
  const [deleteIngestedDocuments] = useDeleteIngestedDocumentsMutation();
  const [submitKBApproval] = useSubmitKBApprovalMutation();
  const navigate = useNavigate();
  const { isKBApprovalFilesEnabled, isKBTagEnabled, isMinervaKbEnabled } =
    useSelectedTeam();
  const isUserCriticalInTeam = useAppSelector(
    (state) => state.teamSettings.isUserCriticalInTeam,
  );
  const userAlias = useAppSelector((state) => state.auth.user.alias);
  const defaultTeamDocType = useDefaultDocumentType(category);

  const showKBNotificationsIfNeeded = (result: {
    invalidatedKBApprovalDocuments?: boolean;
    requiresReIngestion?: boolean;
  }) => {
    if (result.invalidatedKBApprovalDocuments) {
      addNotification({
        type: "info",
        header: "Pending KB approval has been updated.",
        content: (
          <span>
            Visit the{" "}
            <Link
              href={`/reviews?tabId=${KB_APPROVALS_TAB_ID}`}
              onFollow={(e) => {
                e.preventDefault();
                navigate(`/reviews?tabId=${KB_APPROVALS_TAB_ID}`);
              }}
            >
              Reviews page
            </Link>{" "}
            for details.
          </span>
        ),
        dismissible: true,
        timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
      });
    }
    if (result.requiresReIngestion) {
      addNotification({
        type: "warning",
        header:
          "One or more documents are in the Answers KB. Re-submit them for ingestion to update the KB with the latest content.",
        dismissible: true,
        timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
      });
    }
  };

  /**
   * Download a single document
   */
  const downloadSingleDoc = useCallback(
    async (doc: Document, versionId?: string) => {
      try {
        if (doc.s3Key) {
          const request = {
            documentId: doc.documentId,
            preview: false,
            ...(versionId && { versionId }),
          };
          const response = await lazyDownloadUrlCall(request).unwrap();

          if (response?.url) {
            window.location.href = response.url;
          }
        }
      } catch (_e) {
        addNotification({
          type: "error",
          header: "File download failed.",
          dismissible: true,
          timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
        });
      }
    },
    [lazyDownloadUrlCall, addNotification],
  );

  /**
   * Download multiple documents
   */
  const downloadDocs = useCallback(
    async (selectedItems: Document[]) => {
      for (const doc of selectedItems) {
        await downloadSingleDoc(doc);
        await sleep(500); // Rate limiting
      }
    },
    [downloadSingleDoc],
  );

  /**
   * Rename a document (updates fileName)
   */
  const renameDocument = useCallback(
    async (doc: Document, newFileName: string) => {
      try {
        const result = await batchUpdateDocuments({
          updates: [
            {
              documentId: doc.documentId,
              fileName: newFileName,
            },
          ],
        }).unwrap();

        if (result.succeededDocuments.length > 0) {
          addNotification({
            type: "success",
            header: isLink(doc)
              ? "Link updated. Table will update shortly."
              : "File updated. Table will update shortly.",
            dismissible: true,
            timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
          });
          showKBNotificationsIfNeeded(result);
          return true;
        }
        throw new Error("Update failed");
      } catch (e) {
        const fallback = isLink(doc)
          ? "Link update failed."
          : "File update failed.";
        addNotification({
          type: "error",
          header: getDocumentErrorMessage(e, fallback),
          dismissible: true,
          timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
        });
        return false;
      }
    },
    [batchUpdateDocuments, addNotification],
  );

  /**
   * Upload files and register them as documents
   */
  const uploadFiles = useCallback(
    async (
      files: File[] | Array<{ file: File; documentType: string }>,
      documentType: string = defaultTeamDocType,
      progressCallback?: (progress: UploadProgress) => void,
      cancelToken?: CancelToken,
    ) => {
      let successCount = 0;
      let failedCount = 0;

      try {
        const documentsToRegister = [];

        // Upload each file to S3
        for (let i = 0; i < files.length; i++) {
          // Check for cancellation before processing each file
          if (cancelToken?.cancelled) {
            break;
          }

          const entry = files[i];
          const file = entry instanceof File ? entry : entry.file;
          const fileDocumentType =
            entry instanceof File ? documentType : entry.documentType;

          try {
            // Update progress: starting new file
            if (progressCallback) {
              progressCallback({
                isUploading: true,
                currentFileIndex: i + 1,
                totalFiles: files.length,
                currentFileName: file.name,
                currentFileProgress: 0,
                successCount,
                failedCount,
                cancelRequested: cancelToken?.cancelled || false,
              });
            }

            const request = createUploadUrlRequest(
              `${category}/${parentId}`,
              file.name,
              teamName,
              "Normal",
            );
            const uploadUrlResponse = await uploadUrlCall(request).unwrap();

            if (!uploadUrlResponse) {
              throw new Error("Failed to get upload URL");
            }

            // Upload to S3 with progress tracking
            await uploadFileWithProgress(
              uploadUrlResponse.url,
              file,
              uploadUrlResponse.bucketOwner,
              (percentage) => {
                if (progressCallback) {
                  progressCallback({
                    isUploading: true,
                    currentFileIndex: i + 1,
                    totalFiles: files.length,
                    currentFileName: file.name,
                    currentFileProgress: percentage,
                    successCount,
                    failedCount,
                    cancelRequested: cancelToken?.cancelled || false,
                  });
                }
              },
              cancelToken,
            );

            // Prepare document registration
            const s3Key = buildS3Key(
              `${category}/${parentId}`,
              uploadUrlResponse.uniqueKey,
              file.name,
            );

            documentsToRegister.push({
              fileName: getFileNameWithoutExtension(file.name),
              extensionType: getFileExtension(file.name),
              parentId: parentId,
              teamName: teamName,
              gaiaObjectId: gaiaObjectId,
              s3Key: s3Key,
              size: getFileSize(file.size),
              documentType: fileDocumentType,
              securityLevel: "Normal",
            });

            successCount++;
          } catch (fileError) {
            console.error(`Failed to upload ${file.name}:`, fileError);
            failedCount++;
            // Continue with next file
          }
        }

        // Batch register documents
        const batches = batchItems(
          documentsToRegister,
          GaiaConstants.DOCUMENT_API_BATCH_SIZE,
        );
        let allSucceededDocs: UploadedDocument[] = [];
        let totalFailed = 0;
        let hasInvalidatedKB = false;
        let hasRequiresReIngestion = false;

        for (const batch of batches) {
          const result = await batchRegisterDocuments({
            documents: batch,
          }).unwrap();

          const failedSet = new Set(result.failedDocuments);
          const succeededBatch = batch.filter(
            (doc) => !failedSet.has(doc.fileName + doc.extensionType),
          );
          const succeededDocs = result.succeededDocuments.map(
            (id: string, i: number) => ({
              documentId: id,
              fileName: succeededBatch[i]?.fileName || "",
              extensionType: succeededBatch[i]?.extensionType || "",
            }),
          );
          allSucceededDocs = allSucceededDocs.concat(succeededDocs);
          totalFailed += result.failedDocuments.length;
          if (result.invalidatedKBApprovalDocuments) hasInvalidatedKB = true;
          if (result.requiresReIngestion) hasRequiresReIngestion = true;
        }

        if (totalFailed > 0) {
          addNotification({
            type: "warning",
            header: `${allSucceededDocs.length} file${
              allSucceededDocs.length !== 1 ? "s" : ""
            } uploaded. ${totalFailed} failed.`,
            dismissible: true,
            timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
          });
        } else {
          addNotification({
            type: "success",
            header: `${allSucceededDocs.length} file${
              allSucceededDocs.length !== 1 ? "s" : ""
            } uploaded successfully.`,
            dismissible: true,
            timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
          });
        }
        showKBNotificationsIfNeeded({
          invalidatedKBApprovalDocuments: hasInvalidatedKB,
          requiresReIngestion: hasRequiresReIngestion,
        });

        return allSucceededDocs;
      } catch (e) {
        addNotification({
          type: "error",
          header: getDocumentErrorMessage(e, "File upload failed."),
          dismissible: true,
          timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
        });
        return [];
      }
    },
    [
      uploadUrlCall,
      batchRegisterDocuments,
      teamName,
      parentId,
      gaiaObjectId,
      category,
      addNotification,
      defaultTeamDocType,
    ],
  );

  /**
   * Create a folder
   */
  const createFolder = useCallback(
    async (folderName: string) => {
      try {
        const result = await batchRegisterDocuments({
          documents: [
            {
              fileName: folderName,
              extensionType: GaiaConstants.FOLDER_EXTENSION_TYPE,
              parentId: parentId,
              teamName: teamName,
              gaiaObjectId: gaiaObjectId,
              documentType: "Team",
              securityLevel: "Normal",
            },
          ],
        }).unwrap();

        if (result.succeededDocuments.length > 0) {
          addNotification({
            type: "success",
            header: "Folder created successfully. Table will update shortly.",
            dismissible: true,
            timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
          });
          return true;
        }
        throw new Error("Folder creation failed");
      } catch (e) {
        addNotification({
          type: "error",
          header: getDocumentErrorMessage(e, "Folder creation failed."),
          dismissible: true,
          timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
        });
        return false;
      }
    },
    [batchRegisterDocuments, teamName, parentId, gaiaObjectId, addNotification],
  );

  /**
   * Delete entire documents (all versions) - soft delete
   * Sets deleted=true and preserves all version history for audit trail
   * Typically used from table bulk actions
   */
  const deleteDocuments = useCallback(
    async (docs: Document[]) => {
      try {
        const deletions = docs.map((doc) => ({
          documentId: doc.documentId,
          allVersions: true,
        }));

        // Batch deletions
        const batches = batchItems(
          deletions,
          GaiaConstants.DOCUMENT_API_BATCH_SIZE,
        );
        let totalSucceeded = 0;
        let totalFailed = 0;
        let hasInvalidatedKB = false;
        let hasRequiresReIngestion = false;

        for (const batch of batches) {
          const result = await batchDeleteDocuments({
            deletions: batch,
          }).unwrap();

          totalSucceeded += result.succeededDocuments.length;
          totalFailed += result.failedDocuments.length;
          if (result.invalidatedKBApprovalDocuments) hasInvalidatedKB = true;
          if (result.requiresReIngestion) hasRequiresReIngestion = true;
        }

        if (totalFailed > 0) {
          addNotification({
            type: "warning",
            header: `${totalSucceeded} item${
              totalSucceeded !== 1 ? "s" : ""
            } deleted. ${totalFailed} failed. Table will update shortly.`,
            dismissible: true,
            timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
          });
        } else {
          addNotification({
            type: "success",
            header: `${totalSucceeded} item${
              totalSucceeded !== 1 ? "s" : ""
            } deleted successfully. Table will update shortly.`,
            dismissible: true,
            timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
          });
        }
        showKBNotificationsIfNeeded({
          invalidatedKBApprovalDocuments: hasInvalidatedKB,
          requiresReIngestion: hasRequiresReIngestion,
        });

        return totalSucceeded > 0;
      } catch (_e) {
        addNotification({
          type: "error",
          header: "Delete operation failed.",
          dismissible: true,
          timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
        });
        return false;
      }
    },
    [batchDeleteDocuments, addNotification],
  );

  /**
   * Delete a specific version by versionId - hard delete
   * Removes the version from the versions array
   * If deleting current version, promotes next version to current
   * Typically used from file preview panel
   */
  const deleteVersion = useCallback(
    async (documentId: string, versionId: string) => {
      try {
        const result = await batchDeleteDocuments({
          deletions: [
            {
              documentId: documentId,
              versionId: versionId,
              allVersions: false, // Ignored when versionId is provided
            },
          ],
        }).unwrap();

        if (result.succeededDocuments.length > 0) {
          addNotification({
            type: "success",
            header: "Version deleted successfully. Table will update shortly.",
            dismissible: true,
            timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
          });
          showKBNotificationsIfNeeded(result);
          return true;
        }
        throw new Error("Delete version failed");
      } catch (_e) {
        addNotification({
          type: "error",
          header: "Delete version failed.",
          dismissible: true,
          timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
        });
        return false;
      }
    },
    [batchDeleteDocuments, addNotification],
  );

  /**
   * Delete current version only
   * If versions array is empty → soft delete (deleted=true)
   * If has versions → promotes first version from array to current
   * Typically used when user wants to revert to an older version
   */
  const deleteCurrentVersion = useCallback(
    async (documentId: string) => {
      try {
        const result = await batchDeleteDocuments({
          deletions: [
            {
              documentId: documentId,
              allVersions: false,
            },
          ],
        }).unwrap();

        if (result.succeededDocuments.length > 0) {
          addNotification({
            type: "success",
            header:
              "Current version deleted successfully. Table will update shortly.",
            dismissible: true,
            timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
          });
          showKBNotificationsIfNeeded(result);
          return true;
        }
        throw new Error("Delete current version failed");
      } catch (_e) {
        addNotification({
          type: "error",
          header: "Delete current version failed.",
          dismissible: true,
          timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
        });
        return false;
      }
    },
    [batchDeleteDocuments, addNotification],
  );

  /**
   * Move files to a different parent
   */
  const moveFiles = useCallback(
    async (files: Document[], targetParentId: string) => {
      try {
        const updates = files.map((file) => ({
          documentId: file.documentId,
          parentId: targetParentId,
        }));

        // Batch updates
        const batches = batchItems(
          updates,
          GaiaConstants.DOCUMENT_API_BATCH_SIZE,
        );
        let totalSucceeded = 0;
        let totalFailed = 0;
        let hasInvalidatedKB = false;
        let hasRequiresReIngestion = false;

        for (const batch of batches) {
          const result = await batchUpdateDocuments({
            updates: batch,
          }).unwrap();

          totalSucceeded += result.succeededDocuments.length;
          totalFailed += result.failedDocuments.length;
          if (result.invalidatedKBApprovalDocuments) hasInvalidatedKB = true;
          if (result.requiresReIngestion) hasRequiresReIngestion = true;
        }

        if (totalFailed > 0) {
          addNotification({
            type: "warning",
            header: `${totalSucceeded} file${
              totalSucceeded !== 1 ? "s" : ""
            } moved. ${totalFailed} failed. Table will update shortly.`,
            dismissible: true,
            timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
          });
        } else {
          addNotification({
            type: "success",
            header: `${totalSucceeded} file${
              totalSucceeded !== 1 ? "s" : ""
            } moved successfully. Table will update shortly.`,
            dismissible: true,
            timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
          });
        }
        showKBNotificationsIfNeeded({
          invalidatedKBApprovalDocuments: hasInvalidatedKB,
          requiresReIngestion: hasRequiresReIngestion,
        });

        return totalSucceeded > 0;
      } catch (e) {
        addNotification({
          type: "error",
          header: getDocumentErrorMessage(e, "Move operation failed."),
          dismissible: true,
          timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
        });
        return false;
      }
    },
    [batchUpdateDocuments, addNotification],
  );

  /**
   * Add a link (external URL document)
   */
  const addLink = useCallback(
    async (
      fileName: string,
      externalUrl: string,
      documentType: string = defaultTeamDocType,
    ) => {
      try {
        const result = await batchRegisterDocuments({
          documents: [
            {
              fileName: fileName,
              extensionType: GaiaConstants.LINK_EXTENSION_TYPE,
              parentId: parentId,
              teamName: teamName,
              gaiaObjectId: gaiaObjectId,
              documentType: documentType,
              securityLevel: "Normal",
              externalUrl: externalUrl,
            },
          ],
        }).unwrap();

        if (result.succeededDocuments.length > 0) {
          addNotification({
            type: "success",
            header: "Link added successfully.",
            dismissible: true,
            timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
          });
          return true;
        }
        throw new Error("Add link failed");
      } catch (e) {
        addNotification({
          type: "error",
          header: getDocumentErrorMessage(e, "Add link failed."),
          dismissible: true,
          timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
        });
        return false;
      }
    },
    [
      batchRegisterDocuments,
      teamName,
      parentId,
      gaiaObjectId,
      addNotification,
      defaultTeamDocType,
    ],
  );

  /**
   * Update document type
   */
  const updateDocumentType = useCallback(
    async (doc: Document, newType: string) => {
      try {
        const result = await batchUpdateDocuments({
          updates: [
            {
              documentId: doc.documentId,
              documentType: newType,
            },
          ],
        }).unwrap();

        if (result.succeededDocuments.length > 0) {
          addNotification({
            type: "success",
            header: "Document type updated successfully.",
            dismissible: true,
            timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
          });
          showKBNotificationsIfNeeded(result);
          return true;
        }
        throw new Error("Update failed");
      } catch (e) {
        addNotification({
          type: "error",
          header: getDocumentErrorMessage(e, "Document type update failed."),
          dismissible: true,
          timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
        });
        return false;
      }
    },
    [batchUpdateDocuments, addNotification],
  );

  const updateDocumentDescription = useCallback(
    async (doc: Document, newDescription: string) => {
      try {
        const result = await batchUpdateDocuments({
          updates: [
            {
              documentId: doc.documentId,
              description: newDescription,
            },
          ],
        }).unwrap();

        if (result.succeededDocuments.length > 0) {
          addNotification({
            type: "success",
            header: "Document description updated successfully.",
            dismissible: true,
            timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
          });
          return true;
        }
        throw new Error("Update failed");
      } catch (e) {
        addNotification({
          type: "error",
          header: getDocumentErrorMessage(
            e,
            "Document description update failed.",
          ),
          dismissible: true,
          timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
        });
        return false;
      }
    },
    [batchUpdateDocuments, addNotification],
  );

  /**
   * Upload ZIP file for Team Files import
   * Uploads to team-files-imports prefix in etl-temp-bucket
   */
  const uploadZipForImport = useCallback(
    async (
      file: File,
      progressCallback?: (percentage: number) => void,
      cancelToken?: CancelToken,
    ) => {
      try {
        // Get upload URL for team-files-imports prefix
        const request = createUploadUrlRequest(
          "team-files-imports",
          file.name,
          teamName,
          "Normal",
        );
        const uploadUrlResponse = await uploadUrlCall(request).unwrap();

        if (!uploadUrlResponse) {
          throw new Error("Failed to get upload URL");
        }

        // Upload ZIP to S3 with progress tracking
        await uploadFileWithProgress(
          uploadUrlResponse.url,
          file,
          uploadUrlResponse.bucketOwner,
          progressCallback || (() => {}),
          cancelToken,
        );

        // Build S3 key for API call
        const zipS3Key = buildS3Key(
          "team-files-imports",
          uploadUrlResponse.uniqueKey,
          file.name,
        );

        return { zipS3Key };
      } catch (_e) {
        addNotification({
          type: "error",
          header: "ZIP upload failed.",
          content: "Failed to upload ZIP file. Please try again.",
          dismissible: true,
          timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
        });
        return null;
      }
    },
    [uploadUrlCall, teamName, addNotification],
  );

  /**
   * Restore a version by creating a new document pointing to the version's S3 key
   * This makes the selected version become the current version
   */
  const restoreVersion = useCallback(
    async (doc: Document, versionToRestore: any) => {
      try {
        const result = await batchRegisterDocuments({
          documents: [
            {
              fileName: doc.fileName,
              extensionType: doc.extensionType,
              parentId: doc.parentId,
              teamName: doc.teamName,
              gaiaObjectId: doc.gaiaObjectId,
              s3Key: versionToRestore.s3Key,
              size: versionToRestore.size,
              documentType: doc.documentType,
              securityLevel: doc.securityLevel,
            },
          ],
        }).unwrap();

        if (result.succeededDocuments.length > 0) {
          addNotification({
            type: "success",
            header: "Version restored successfully. Table will update shortly.",
            dismissible: true,
            timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
          });
          showKBNotificationsIfNeeded(result);
          return true;
        }
        throw new Error("Restore failed");
      } catch (e) {
        addNotification({
          type: "error",
          header: getDocumentErrorMessage(e, "Version restore failed."),
          dismissible: true,
          timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
        });
        return false;
      }
    },
    [batchRegisterDocuments, teamName, gaiaObjectId, addNotification],
  );

  const ingestToGaiaKB = useCallback(
    async (docs: Document[]) => {
      if (!docs.length || !teamName) return;

      // KB-ingestion guard: Excel (.xlsx/.xls) is never indexable by the Gaia
      // knowledge base, so it is filtered out here UNCONDITIONALLY — unlike the
      // Answers guard, this is NOT gated on `isMinervaKbEnabled`. This is the
      // single choke point to the Gaia KB ingest API (toolbar Actions → "Gaia
      // knowledge base"), so guarding here covers every entry point. Excel
      // remains fully uploadable to the document store (the upload path is
      // untouched) — only KB ingestion is blocked. Without this guard the
      // backend returns Excel in `failedDocuments`, producing a generic red
      // error; instead we surface a graceful info notice.
      const ingestibleDocs = docs.filter(
        (d) => !isExcelExtension(d.extensionType),
      );
      const excludedExcelCount = docs.length - ingestibleDocs.length;

      // All selected docs were Excel: nothing left to ingest. Surface the
      // "not supported" notice and return cleanly — matching the empty-guard
      // early return so no spinner/half-state is left behind (no ingest call).
      if (ingestibleDocs.length === 0) {
        addNotification(
          buildExcelNotAddedNotification(
            excludedExcelCount,
            GAIA_EXCEL_KB_NOT_SUPPORTED,
          ),
        );
        return;
      }

      // Partial: some (but not all) docs were Excel. Proceed to ingest the
      // non-Excel docs below and surface a notice that the Excel files were
      // skipped.
      if (excludedExcelCount > 0) {
        addNotification(
          buildExcelNotAddedNotification(
            excludedExcelCount,
            GAIA_EXCEL_KB_NOT_SUPPORTED,
          ),
        );
      }

      try {
        const result = await ingestDocuments({
          teamName,
          documentSource: "GAIA_OBJECTS",
          targetKB: "GAIA",
          documentIngestions: {
            items: ingestibleDocs.map((item) => ({
              documentId: item.documentId,
            })),
          },
        }).unwrap();

        if (result.failedDocuments?.length > 0) {
          addNotification({
            type: "error",
            content: `Failed to initiate indexing for ${
              result.failedDocuments.length
            } document${result.failedDocuments.length > 1 ? "s" : ""}.`,
            dismissible: true,
          });
        } else {
          addNotification({
            type: "success",
            content: `Indexing into Gaia knowledge base initiated for ${
              result.succeededDocuments.length
            } document${result.succeededDocuments.length > 1 ? "s" : ""}.`,
            dismissible: true,
            timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
          });
        }
      } catch {
        addNotification({
          type: "error",
          content: "Failed to initiate indexing. Please try again.",
          dismissible: true,
        });
      }
    },
    [ingestDocuments, teamName, addNotification],
  );

  const deleteFromGaiaKB = useCallback(
    async (docs: Document[]) => {
      if (!docs.length || !teamName) return;
      try {
        const result = await deleteIngestedDocuments({
          teamName,
          documents: docs.map((item) => item.documentId),
          documentSource: "GAIA_OBJECTS",
        }).unwrap();

        if (result.failedDocuments?.length > 0) {
          addNotification({
            type: "error",
            content: `Failed to delete ${
              result.failedDocuments.length
            } document${result.failedDocuments.length > 1 ? "s" : ""} from knowledge base.`,
            dismissible: true,
          });
        } else {
          addNotification({
            type: "success",
            content: `${
              result.succeededDocuments.length
            } document${result.succeededDocuments.length > 1 ? "s" : ""} deleted from knowledge base. Table will update shortly.`,
            dismissible: true,
            timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
          });
        }
      } catch {
        addNotification({
          type: "error",
          content: "Failed to delete from knowledge base. Please try again.",
          dismissible: true,
        });
      }
    },
    [deleteIngestedDocuments, teamName, addNotification],
  );

  const ingestToAnswersKB = useCallback(
    async (docs: Document[]) => {
      if (!docs.length || !teamName) return;

      // KB-ingestion guard: keep only Answers-KB-supported extensions. This is
      // the single choke point to the KB-ingest API, so guarding here covers
      // BOTH the upload-modal "Answers knowledge base" checkbox and the toolbar
      // "ingest selected docs" action. Uses the SAME predicate as the sibling
      // `ingestFoldersToAnswersKB` folder guard so the two Answers paths stay in
      // agreement: `isAnswersKbSupportedExtension` drops ANY unsupported
      // extension (and a doc with no extension), and treats `.xls` as
      // unsupported even on Minerva teams (only `.xlsx` is in the accept list).
      // Because the filter is not Excel-specific, the "not added" notice below
      // uses GENERIC unsupported-file wording so a non-Excel unsupported file is
      // never mislabeled as "Excel" — consistent with the folder guard's
      // skipped-unsupported copy. Files remain fully uploadable to the document
      // store (the upload path is untouched) — only KB ingestion is blocked.
      const ingestibleDocs = docs.filter((d) =>
        isAnswersKbSupportedExtension(d.extensionType, isMinervaKbEnabled),
      );
      const skippedUnsupportedCount = docs.length - ingestibleDocs.length;

      // All selected docs were unsupported: nothing left to ingest. Surface the
      // "not added" notice and return cleanly — matching the empty-guard early
      // return so no spinner/half-state is left behind (no ingest call, no
      // approval navigation).
      if (ingestibleDocs.length === 0) {
        addNotification(
          buildUnsupportedNotAddedNotification(
            skippedUnsupportedCount,
            ANSWERS_UNSUPPORTED_KB_NOT_SUPPORTED,
          ),
        );
        return;
      }

      // Partial: some (but not all) docs were unsupported. Proceed to ingest the
      // supported docs below and surface a notice that the unsupported files
      // were skipped, consistent with the folder guard's skipped-unsupported
      // copy.
      if (skippedUnsupportedCount > 0) {
        addNotification(
          buildUnsupportedNotAddedNotification(
            skippedUnsupportedCount,
            ANSWERS_UNSUPPORTED_KB_NOT_SUPPORTED_PARTIAL,
          ),
        );
      }

      const existingDocumentIds = ingestibleDocs.map((d) => d.documentId);

      if (!isKBApprovalFilesEnabled) {
        addNotification({
          type: "info",
          header:
            existingDocumentIds.length === 1
              ? "Submitting document for ingestion..."
              : "Submitting documents for ingestion...",
          dismissible: true,
          timeout: GaiaConstants.NOTIFICATION_TIMEOUT.SHORT,
        });
        try {
          await ingestDocuments({
            teamName,
            documentSource: "GAIA_OBJECTS",
            targetKB: "ANSWERS",
            documentIngestions: {
              items: ingestibleDocs.map((d) => ({ documentId: d.documentId })),
            },
          }).unwrap();
          addNotification({
            type: "success",
            header:
              existingDocumentIds.length === 1
                ? "Document submitted for ingestion."
                : "Documents submitted for ingestion.",
            dismissible: true,
            timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
          });
        } catch {
          addNotification({
            type: "error",
            header:
              existingDocumentIds.length === 1
                ? "Failed to submit document for ingestion."
                : "Failed to submit documents for ingestion.",
            dismissible: true,
            timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
          });
        }
      } else if (isUserCriticalInTeam) {
        addNotification({
          type: "info",
          header:
            existingDocumentIds.length === 1
              ? "Submitting document for ingestion..."
              : "Submitting documents for ingestion...",
          dismissible: true,
          timeout: GaiaConstants.NOTIFICATION_TIMEOUT.SHORT,
        });
        try {
          await submitKBApproval({
            teamName,
            existingDocumentIds,
            approvers: [userAlias],
            approvalMode: "OR",
          }).unwrap();
          addNotification({
            type: "success",
            header:
              existingDocumentIds.length === 1
                ? "Document submitted for ingestion."
                : "Documents submitted for ingestion.",
            dismissible: true,
            timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
          });
        } catch (error: any) {
          addNotification({
            type: "error",
            header:
              existingDocumentIds.length === 1
                ? "Failed to submit document for ingestion."
                : "Failed to submit documents for ingestion.",
            content: error?.data?.message || undefined,
            dismissible: true,
            timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
          });
        }
      } else {
        const fileNames: Record<string, string> = {};
        const fileSizes: Record<string, string> = {};
        for (const doc of ingestibleDocs) {
          fileNames[doc.documentId] = doc.fileName
            ? `${doc.fileName}${doc.extensionType ?? ""}`
            : doc.documentId;
          // Blank-cell contract: only populate when the source has a size.
          // Missing entries render as an empty cell rather than a sentinel.
          if (doc.size) fileSizes[doc.documentId] = doc.size;
        }
        addNotification({
          type: "info",
          header:
            existingDocumentIds.length === 1
              ? "File selected for ingestion into Answers knowledge base. Select approvers to submit for review."
              : "Files selected for ingestion into Answers knowledge base. Select approvers to submit for review.",
          dismissible: true,
          timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
        });
        navigate("/kb/existing-doc-approval/new", {
          state: {
            existingDocumentIds,
            teamName,
            fileNames,
            fileSizes,
          } as ExistingDocApprovalRouteState,
        });
      }
    },
    [
      isKBApprovalFilesEnabled,
      isUserCriticalInTeam,
      isMinervaKbEnabled,
      ingestDocuments,
      submitKBApproval,
      teamName,
      userAlias,
      addNotification,
      navigate,
    ],
  );

  const ingestFoldersToAnswersKB = useCallback(
    async (
      folders: Document[],
      onProgress?: (p: IngestProgress) => void,
    ): Promise<void> => {
      if (!folders.length || !teamName) return;

      const PAGE_SIZE = 500;

      // Initial discovery emit - before any folder has been walked. Gives
      // the modal a 0% baseline for the discovery bar.
      onProgress?.({
        phase: "discovering",
        foldersDiscovered: 0,
        folderCount: folders.length,
      });

      // Discovery: paginate SearchDocuments per folder, dedup by documentId.
      const discovered = new Map<string, Document>();
      try {
        let foldersDiscovered = 0;
        for (const folder of folders) {
          let searchAfter: string[] | undefined;
          do {
            // parentId must be the folder path (ancestor fileNames joined with '/'),
            // not a documentId. Backend recursive search does STARTS_WITH on DDB_SK,
            // which is stored as a path.
            const response = await searchDocuments({
              teamName,
              parentId: `${folder.parentId || teamName}/${folder.fileName}`,
              recursiveSearch: true,
              pageSize: PAGE_SIZE,
              searchAfter,
              excludeFields: ["summary", "description", "versions"],
              propertyFilterQuery: {
                operation: "and",
                tokens: [
                  {
                    propertyKey: "isFolder",
                    operator: "!=",
                    value: "true",
                  },
                ],
              },
            }).unwrap();
            for (const doc of response.documents) {
              discovered.set(doc.documentId, doc);
            }
            searchAfter = response.paginationMetadata?.searchAfter;
          } while (searchAfter && searchAfter.length > 0);
          // Per-folder discovery tick: emit AFTER the inner pagination loop
          // fully drains so foldersDiscovered reflects folders whose files
          // are now all in the `discovered` map.
          foldersDiscovered += 1;
          onProgress?.({
            phase: "discovering",
            foldersDiscovered,
            folderCount: folders.length,
          });
        }
      } catch (_e) {
        addNotification({
          type: "error",
          header: "Failed to list folder contents.",
          content: "Please try again.",
          dismissible: true,
          timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
        });
        return;
      }

      // Client-side filter (per design filter/eligibility rules).
      const eligible: Document[] = [];
      let skippedAlreadyInKB = 0;
      let skippedUnsupported = 0;
      let skippedLinks = 0;

      for (const doc of discovered.values()) {
        if (doc.isFolder) {
          // Defensive: PFQ excludes folders; drop silently if any slip through.
          continue;
        }
        if (doc.externalUrl) {
          skippedLinks += 1;
          continue;
        }
        if (doc.atlasObjectKey) {
          skippedAlreadyInKB += 1;
          continue;
        }
        if (
          !isAnswersKbSupportedExtension(doc.extensionType, isMinervaKbEnabled)
        ) {
          // Excel files are unsupported for non-Minerva teams, so they are
          // counted here and surfaced via the existing "skipped unsupported"
          // completion notification.
          skippedUnsupported += 1;
          continue;
        }
        eligible.push(doc);
      }

      // Build skip line only mentioning non-zero reasons. Shared across every
      // outcome path so the completion toast stays consistent regardless of
      // which dispatch branch runs.
      const skipParts = buildSkipParts({
        skippedAlreadyInKB,
        skippedUnsupported,
        skippedLinks,
      });
      const skipCountTotal =
        skippedAlreadyInKB + skippedUnsupported + skippedLinks;
      const skippedLine =
        skipCountTotal > 0
          ? `Skipped ${skipCountTotal} files (${skipParts.join(", ")}).`
          : "";

      // Zero-eligibility: everything got filtered out. No submission, no
      // navigation, no modal ingesting phase.
      if (eligible.length === 0) {
        if (skipCountTotal === 0) {
          addNotification({
            type: "info",
            header: "No eligible files to ingest.",
            dismissible: true,
            timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
          });
        } else {
          addNotification({
            type: "info",
            header: "No eligible files to ingest.",
            content: `Nothing was submitted from the selected folder(s). Reasons: ${skipParts.join(", ")}.`,
            dismissible: true,
            timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
          });
        }
        return;
      }

      // Submission cap: the KB approval wizard and the direct-ingest path
      // both refuse >500 files per submission. Warn and bail before any
      // side effect (no navigation, no ingest call).
      if (eligible.length > 500) {
        addNotification({
          type: "warning",
          header: "Submission limit exceeded",
          content: `Selected folders contain ${eligible.length} eligible files; the submission limit is 500. Deselect some folders or submit fewer files at a time.`,
          dismissible: true,
          timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
        });
        return;
      }

      // 4-way dispatch on isKBTagEnabled x isKBApprovalFilesEnabled.
      // Tag-off + approval-off is the only branch that submits inline;
      // every other combo hands off to ExistingDocApprovalHost, which
      // owns its own submit/ingest routing.
      if (!isKBTagEnabled && !isKBApprovalFilesEnabled) {
        // Start toast (singular/plural on folder count - only place that
        // varies). Fires only here: this is the sole branch that ingests
        // inline. The other combos navigate to the approval wizard where
        // nothing is ingested yet, so the "Ingesting..." copy would mislead.
        addNotification({
          type: "info",
          header:
            folders.length === 1
              ? "Ingesting folder into Answers knowledge base..."
              : "Ingesting folders into Answers knowledge base...",
          dismissible: true,
          timeout: GaiaConstants.NOTIFICATION_TIMEOUT.SHORT,
        });

        // Kick off the ingesting phase - modal flips to the terminal
        // spinner state until the single ingestDocuments call settles.
        onProgress?.({
          phase: "ingesting",
          eligibleCount: eligible.length,
          skippedAlreadyInKB,
          skippedUnsupported,
          skippedLinks,
        });

        let succeededCount = 0;
        let failedCount = 0;
        let deferredCount = 0;
        try {
          const result = await ingestDocuments({
            teamName,
            documentSource: "GAIA_OBJECTS",
            targetKB: "ANSWERS",
            documentIngestions: {
              items: eligible.map((d) => ({ documentId: d.documentId })),
            },
          }).unwrap();
          succeededCount = result.succeededDocuments?.length ?? 0;
          failedCount = result.failedDocuments?.length ?? 0;
          // Missing docs => deferred (scan pending or blocked).
          deferredCount = eligible.length - succeededCount - failedCount;
        } catch (_e) {
          // Request-level HTTP error: bucket every doc as failed so the
          // toast surfaces the failure rather than reporting phantom success.
          failedCount = eligible.length;
        }

        const deferredLine =
          deferredCount > 0
            ? `${deferredCount} deferred (scan pending or blocked); scan-pending files will be ingested automatically when scanning completes.`
            : "";
        const submittedCount = succeededCount + deferredCount;

        // All-eligible failed → hard error, no partial-success semantics.
        // The whole submission collapsed (RPC threw or every doc came back
        // in failedDocuments), so surface as error rather than warning.
        if (failedCount === eligible.length) {
          addNotification({
            type: "error",
            header:
              eligible.length === 1
                ? "Failed to submit document for ingestion."
                : `Failed to submit all ${eligible.length} documents for ingestion.`,
            dismissible: true,
            timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
          });
          return;
        }

        if (failedCount > 0) {
          const parts: string[] = [];
          if (skippedLine) parts.push(skippedLine);
          if (deferredLine) parts.push(deferredLine);
          parts.push(
            "Failed files remain in Team Files. Retry from the documents table.",
          );
          addNotification({
            type: "warning",
            header: (
              <>
                Submitted {succeededCount + deferredCount} files for ingestion
                into{" "}
                <Link
                  href="/gaia-ai"
                  onFollow={(e) => {
                    e.preventDefault();
                    navigate("/gaia-ai");
                  }}
                >
                  Answers knowledge base
                </Link>
                . {failedCount} failed to ingest.
              </>
            ),
            content: parts.join(" "),
            dismissible: true,
            timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
          });
          return;
        }

        // Success (no failures)
        const parts: string[] = [];
        if (skippedLine) parts.push(skippedLine);
        if (deferredLine) parts.push(deferredLine);
        addNotification({
          type: "success",
          header: (
            <>
              Submitted {submittedCount} files for ingestion into{" "}
              <Link
                href="/gaia-ai"
                onFollow={(e) => {
                  e.preventDefault();
                  navigate("/gaia-ai");
                }}
              >
                Answers knowledge base
              </Link>
              .
            </>
          ),
          content: parts.length ? parts.join(" ") : undefined,
          dismissible: true,
          timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
        });
        return;
      }

      // Tag-on OR approval-on: hand off to the KB approval wizard. The host
      // page (ExistingDocApprovalHost) runs the 4-way dispatch internally
      // based on the same flags - this callsite is folder-blind on purpose.
      const fileNames: Record<string, string> = {};
      const fileSizes: Record<string, string> = {};
      for (const doc of eligible) {
        fileNames[doc.documentId] = doc.fileName
          ? `${doc.fileName}${doc.extensionType ?? ""}`
          : doc.documentId;
        // Blank-cell contract: only populate when source size is truthy so
        // the wizard renders an empty cell rather than an empty-string sentinel.
        if (doc.size) fileSizes[doc.documentId] = doc.size;
      }
      const routeState: ExistingDocApprovalRouteState = {
        existingDocumentIds: eligible.map((d) => d.documentId),
        teamName,
        fileNames,
        fileSizes,
      };
      navigate("/kb/existing-doc-approval/new", { state: routeState });
    },
    [
      teamName,
      addNotification,
      searchDocuments,
      ingestDocuments,
      navigate,
      isKBTagEnabled,
      isKBApprovalFilesEnabled,
      isMinervaKbEnabled,
    ],
  );

  const deleteFromAnswersKB = useCallback(
    async (docs: Document[]) => {
      const ingested = docs.filter((d) => d.atlasObjectKey);
      if (!ingested.length || !teamName) return;
      const atlasObjectKeys = ingested.map((d) => d.atlasObjectKey!);

      if (!isKBApprovalFilesEnabled) {
        addNotification({
          type: "info",
          header:
            atlasObjectKeys.length === 1
              ? "Submitting document for deletion..."
              : "Submitting documents for deletion...",
          dismissible: true,
          timeout: GaiaConstants.NOTIFICATION_TIMEOUT.SHORT,
        });
        try {
          const result = await deleteIngestedDocuments({
            teamName,
            documents: atlasObjectKeys,
          }).unwrap();
          if (result.failedDocuments?.length > 0) {
            addNotification({
              type: "error",
              header:
                result.failedDocuments.length === 1
                  ? "Failed to delete document from Answers knowledge base."
                  : `Failed to delete ${result.failedDocuments.length} documents from Answers knowledge base.`,
              dismissible: true,
              timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
            });
          } else {
            addNotification({
              type: "success",
              header:
                result.succeededDocuments.length === 1
                  ? "Document deleted from Answers knowledge base. Table will update shortly."
                  : `${result.succeededDocuments.length} documents deleted from Answers knowledge base. Table will update shortly.`,
              dismissible: true,
              timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
            });
          }
        } catch {
          addNotification({
            type: "error",
            header:
              atlasObjectKeys.length === 1
                ? "Failed to submit document for deletion."
                : "Failed to submit documents for deletion.",
            dismissible: true,
            timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
          });
        }
      } else if (isUserCriticalInTeam) {
        addNotification({
          type: "info",
          header:
            atlasObjectKeys.length === 1
              ? "Submitting document for deletion..."
              : "Submitting documents for deletion...",
          dismissible: true,
          timeout: GaiaConstants.NOTIFICATION_TIMEOUT.SHORT,
        });
        try {
          await submitKBApproval({
            teamName,
            category: "Deletion",
            atlasObjectKeys,
            approvers: [userAlias],
            approvalMode: "OR",
          }).unwrap();
          addNotification({
            type: "success",
            header:
              atlasObjectKeys.length === 1
                ? "Document submitted for deletion."
                : "Documents submitted for deletion.",
            dismissible: true,
            timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
          });
        } catch {
          addNotification({
            type: "error",
            header:
              atlasObjectKeys.length === 1
                ? "Failed to submit document for deletion."
                : "Failed to submit documents for deletion.",
            dismissible: true,
            timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
          });
        }
      } else {
        const fileSizes: Record<string, string> = {};
        for (const doc of ingested) {
          if (doc.size) fileSizes[doc.atlasObjectKey!] = doc.size;
        }
        addNotification({
          type: "info",
          header:
            atlasObjectKeys.length === 1
              ? "File selected for deletion. Select approvers to submit for review."
              : "Files selected for deletion. Select approvers to submit for review.",
          dismissible: true,
          timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
        });
        navigate("/kb/deletion-approval/new", {
          state: {
            atlasObjectKeys,
            teamName,
            fileSizes,
          } as DeletionApprovalRouteState,
        });
      }
    },
    [
      isKBApprovalFilesEnabled,
      isUserCriticalInTeam,
      deleteIngestedDocuments,
      submitKBApproval,
      teamName,
      userAlias,
      addNotification,
      navigate,
    ],
  );

  return {
    downloadSingleDoc,
    downloadDocs,
    renameDocument,
    uploadFiles,
    uploadZipForImport,
    createFolder,
    deleteDocuments,
    deleteVersion,
    deleteCurrentVersion,
    moveFiles,
    addLink,
    updateDocumentType,
    updateDocumentDescription,
    restoreVersion,
    ingestToGaiaKB,
    deleteFromGaiaKB,
    ingestToAnswersKB,
    ingestFoldersToAnswersKB,
    deleteFromAnswersKB,
  };
};
